// src/stores/useSNR.ts
import { create } from 'zustand'
import { useTasks } from './useTasks'
import { useSettings } from './useSettings'

interface SNRStore {
  snrPct: number
  signalCount: number
  noiseCount: number
  totalCount: number
  doneCount: number
  unclassifiedCount: number
  status: 'locked-in' | 'getting-there' | 'danger' | 'empty'
  noiseTimerSeconds: number
  noiseBudgetSeconds: number
  budgetAlertSent: boolean
  resetNoiseTimer: () => void
  compute: () => void
}

export const useSNR = create<SNRStore>((set, get) => ({
  snrPct: 0,
  signalCount: 0,
  noiseCount: 0,
  totalCount: 0,
  doneCount: 0,
  unclassifiedCount: 0,
  status: 'empty',
  noiseTimerSeconds: 0,
  noiseBudgetSeconds: 45 * 60,
  budgetAlertSent: false,

  resetNoiseTimer: () => set({ budgetAlertSent: false }),

  compute: () => {
    const tasks = useTasks.getState().tasks
    const settings = useSettings.getState().settings

    const today = new Date().toISOString().split('T')[0]
    const todayTasks = tasks.filter(t => t.date === today)

    const signals = todayTasks.filter(t => t.type === 'signal')
    const noises = todayTasks.filter(t => t.type === 'noise')
    const unclassified = todayTasks.filter(t => t.type === 'unclassified')
    const done = todayTasks.filter(t => t.done)

    const classified = signals.length + noises.length
    const snrPct = classified > 0 ? Math.round((signals.length / classified) * 100) : 0

    let status: SNRStore['status'] = 'empty'
    if (classified === 0) status = 'empty'
    else if (snrPct >= 75) status = 'locked-in'
    else if (snrPct >= 50) status = 'getting-there'
    else status = 'danger'

    const noiseTimerSeconds = noises.reduce((acc, t) => acc + (t.timer_seconds ?? 0), 0)
    const noiseBudgetSeconds = (settings.noise_budget_mins ?? 45) * 60

    set({
      snrPct,
      signalCount: signals.length,
      noiseCount: noises.length,
      totalCount: todayTasks.length,
      doneCount: done.length,
      unclassifiedCount: unclassified.length,
      status,
      noiseTimerSeconds,
      noiseBudgetSeconds,
    })

    if (noiseTimerSeconds >= noiseBudgetSeconds && !get().budgetAlertSent) {
      set({ budgetAlertSent: true })
      console.warn('[SNR] Noise budget exceeded')
    }
  },
}))

// Re-run compute whenever tasks or settings change
useTasks.subscribe(() => useSNR.getState().compute())
useSettings.subscribe(() => useSNR.getState().compute())

// Broadcast updated SNR to squad leaderboard (debounced 10s)
let _broadcastTimer: ReturnType<typeof setTimeout> | null = null

useSNR.subscribe((state, prev) => {
  if (state.snrPct === prev.snrPct) return
  if (_broadcastTimer) clearTimeout(_broadcastTimer)
  _broadcastTimer = setTimeout(async () => {
    try {
      const { useSquads } = await import('./useSquads')
      await useSquads.getState().broadcastMySNR(state.snrPct)
    } catch {
      // Non-critical
    }
  }, 10_000)
})
